import {
  Container,
  TextField,
  Button,
  Grid,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  AlertTitle,
  InputAdornment,
} from "@mui/material";
import {
  BuildCircle,
  Label,
  Event,
  Comment,
  Scale,
  MonetizationOn,
  Person,
  Email,
} from "@mui/icons-material";
import { useState } from "react";
import { RepairForm } from "types/restarter";
import { useRestarterForm } from "hooks/useRestarterForm";
import { getNeutralRepairForm } from "libraries/neutral";
import useRestarterEventLogin from "hooks/restarter/useRestarterEventLogin";
import RestarterEventLoginModal from "components/restarter/RestarterEventLoginModal";
import RestarterPageHeader from "components/restarter/RestarterPageHeader";

const RestarterRepairFormPage = () => {
  const { submitRestarterForm, loading, formSubmitted } = useRestarterForm();
  const { handleLogout, loginEventCode, loginFormOpen, handleLoginToEvent } =
    useRestarterEventLogin();
  const [formData, setFormData] = useState<RepairForm>(getNeutralRepairForm());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const success = await submitRestarterForm({
      ...formData,
      id: (crypto as any).randomUUID(),
      eventCode: loginEventCode,
    });
    success && setFormData(getNeutralRepairForm());
  };

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Box p={2} visibility={loginFormOpen ? "hidden" : "visible"}>
        <RestarterPageHeader
          title="Register your item for repair"
          loginEventCode={loginEventCode}
          onLogout={handleLogout}
        />
        <Box component="form" onSubmit={handleSubmit} mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Item Name"
                name="item"
                value={formData.item}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BuildCircle />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Item Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Label />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Age in years"
                name="age"
                type="number"
                value={formData.age}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Event />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Explain the problem of the item"
                name="assessment"
                multiline
                rows={4}
                required
                value={formData.assessment}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Comment />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Weight of entire item (KG)"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Scale />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price when new"
                name="priceAsNew"
                type="number"
                value={formData.priceAsNew}
                onChange={handleChange}
                required
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MonetizationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fullname"
                name="fullname"
                required
                value={formData.fullname}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    required
                    checked={formData.agreed}
                    onChange={handleChange}
                    name="agreed"
                    color="secondary"
                  />
                }
                label="I confirm that I am responsible if the repair cannot be finished and is or remain broken"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                fullWidth
                disabled={loading || !loginEventCode}
              >
                {loading ? "Submitting..." : "Submit the form"}
              </Button>
            </Grid>
            {formSubmitted && (
              <Grid item xs={12}>
                {formSubmitted === "SUCCESS" ? (
                  <Alert severity="success">
                    <AlertTitle>Success</AlertTitle>
                    Your form has been submitted! You can fill another one.
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    There was an error submitting your form. Please try again.
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
      <RestarterEventLoginModal
        formLabel="Go to Event form"
        isOpen={loginFormOpen}
        onLoginValidation={handleLoginToEvent}
      />
    </Container>
  );
};

export default RestarterRepairFormPage;
